/**
 * CRM Analytics Page — KPI cards, pipeline funnel, activity metrics.
 */

import { useEffect, useState, useCallback } from 'react'
import { BarChart3, TrendingUp, Users, DollarSign, CheckSquare } from 'lucide-react'
import { useCrmStore } from '@/stores/crmStore'
import { useCRM } from '@/hooks/useCRM'
import { formatCents } from '@/utils/currency'
import { Spinner, Progress } from '@/components/ui'
import { apiClient } from '@/services/apiClient'

interface OverviewMetrics {
  openDeals: number
  wonDeals: number
  revenueCents: number
  totalContacts: number
}

interface PipelineFunnelStage {
  stageId: string; stageName: string; color: string; count: number
}

interface ActivityMetric {
  type: string; count: number
}

interface ChannelMetric {
  channel: string; count: number
}

export function AnalyticsPage() {
  const { fetchPipelines } = useCRM()
  const activePipeline = useCrmStore((s) => s.activePipeline)

  const [days, setDays] = useState(30)
  const [overview, setOverview] = useState<OverviewMetrics | null>(null)
  const [funnel, setFunnel] = useState<PipelineFunnelStage[]>([])
  const [activityData, setActivityData] = useState<ActivityMetric[]>([])
  const [contactData, setContactData] = useState<{ total: number; newInPeriod: number; byChannel: ChannelMetric[] }>({ total: 0, newInPeriod: 0, byChannel: [] })
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => { fetchPipelines() }, [fetchPipelines])

  const loadData = useCallback(async () => {
    setIsLoading(true)
    try {
      // Overview
      const ovRes = await apiClient.fetch(`/api/crm/analytics/overview?days=${days}`)
      if (ovRes.ok) {
        const d = await ovRes.json()
        setOverview({ openDeals: d.open_deals, wonDeals: d.won_deals, revenueCents: d.revenue_cents, totalContacts: d.total_contacts })
      }

      // Pipeline funnel
      if (activePipeline) {
        const fRes = await apiClient.fetch(`/api/crm/analytics/pipeline?pipeline_id=${activePipeline.id}`)
        if (fRes.ok) {
          const d = await fRes.json()
          setFunnel((d.stages as Array<Record<string, unknown>>).map((s) => ({
            stageId: s.stage_id as string, stageName: s.stage_name as string,
            color: s.color as string, count: s.count as number,
          })))
        }
      }

      // Activity
      const aRes = await apiClient.fetch(`/api/crm/analytics/activity?days=${days}`)
      if (aRes.ok) {
        const d = await aRes.json()
        setActivityData((d.by_type as Array<Record<string, unknown>>).map((a) => ({
          type: a.type as string, count: a.count as number,
        })))
      }

      // Contacts
      const cRes = await apiClient.fetch(`/api/crm/analytics/contacts?days=${days}`)
      if (cRes.ok) {
        const d = await cRes.json()
        setContactData({
          total: d.total as number, newInPeriod: d.new_in_period as number,
          byChannel: (d.by_channel as Array<Record<string, unknown>>).map((c) => ({
            channel: c.channel as string, count: c.count as number,
          })),
        })
      }
    } catch (e) {
      console.error('Analytics load failed:', e)
    } finally {
      setIsLoading(false)
    }
  }, [days, activePipeline])

  useEffect(() => { loadData() }, [loadData])

  const maxFunnelCount = Math.max(...funnel.map((s) => s.count), 1)

  return (
    <div className="flex flex-col h-full">
      <div className="border-b border-border">
        <div className="py-3 flex items-center gap-3">
          <BarChart3 className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm font-medium text-foreground">Analytics</span>
          <div className="flex-1" />
          <select value={days} onChange={(e) => setDays(Number(e.target.value))}
            className="text-sm bg-muted border border-border rounded-lg px-3 py-2 text-foreground">
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={365}>Last year</option>
          </select>
        </div>
      </div>

      <div className="flex-1 overflow-auto py-6">
        {isLoading ? (
          <div className="flex justify-center py-8"><Spinner /></div>
        ) : (
          <div className="space-y-6">
            {/* KPI Cards */}
            {overview && (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <KpiCard icon={TrendingUp} label="Open Deals" value={String(overview.openDeals)} color="text-blue-500" />
                <KpiCard icon={CheckSquare} label="Won Deals" value={String(overview.wonDeals)} sublabel={`last ${days}d`} color="text-green-500" />
                <KpiCard icon={DollarSign} label="Revenue" value={formatCents(overview.revenueCents)} sublabel={`last ${days}d`} color="text-emerald-500" />
                <KpiCard icon={Users} label="Total Contacts" value={String(overview.totalContacts)} color="text-purple-500" />
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Pipeline Funnel */}
              {funnel.length > 0 && (
                <div className="bg-card border border-border rounded-lg p-5">
                  <h3 className="text-sm font-semibold text-foreground mb-4">Pipeline Funnel</h3>
                  <div className="space-y-3">
                    {funnel.map((stage) => (
                      <div key={stage.stageId} className="flex items-center gap-3">
                        <span className="text-xs text-muted-foreground w-24 truncate">{stage.stageName}</span>
                        <div className="flex-1 h-6 bg-muted rounded-full overflow-hidden">
                          <div className="h-full rounded-full transition-all" style={{
                            width: `${(stage.count / maxFunnelCount) * 100}%`,
                            backgroundColor: stage.color, minWidth: stage.count > 0 ? '8px' : '0',
                          }} />
                        </div>
                        <span className="text-sm font-medium text-foreground w-8 text-right">{stage.count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Activity Breakdown */}
              {activityData.length > 0 && (
                <div className="bg-card border border-border rounded-lg p-5">
                  <h3 className="text-sm font-semibold text-foreground mb-4">Activity Breakdown</h3>
                  <div className="space-y-3">
                    {activityData.map((a) => {
                      const total = activityData.reduce((s, x) => s + x.count, 0)
                      const pct = total > 0 ? Math.round((a.count / total) * 100) : 0
                      return (
                        <div key={a.type} className="flex items-center gap-3">
                          <span className="text-xs text-muted-foreground w-20 capitalize">{a.type}s</span>
                          <Progress value={pct} className="h-5 flex-1" />
                          <span className="text-xs text-muted-foreground w-12 text-right">{a.count} ({pct}%)</span>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}

              {/* Contact Growth */}
              <div className="bg-card border border-border rounded-lg p-5">
                <h3 className="text-sm font-semibold text-foreground mb-4">Contact Growth</h3>
                <div className="flex items-center gap-6 mb-3">
                  <div>
                    <p className="text-2xl font-bold text-foreground">{contactData.total}</p>
                    <p className="text-xs text-muted-foreground">Total contacts</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-success">+{contactData.newInPeriod}</p>
                    <p className="text-xs text-muted-foreground">New (last {days}d)</p>
                  </div>
                </div>
                {contactData.byChannel.length > 0 && (
                  <div className="flex gap-3 mt-3">
                    {contactData.byChannel.map((c) => (
                      <div key={c.channel} className="flex items-center gap-1.5 text-xs">
                        <div className={`w-2.5 h-2.5 rounded-full ${c.channel === 'whatsapp' ? 'bg-green-500' : 'bg-blue-500'}`} />
                        <span className="text-muted-foreground capitalize">{c.channel}: {c.count}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function KpiCard({ icon: Icon, label, value, sublabel, color }: {
  icon: React.ElementType; label: string; value: string; sublabel?: string; color: string
}) {
  return (
    <div className="bg-card border border-border rounded-lg p-4">
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-4 h-4 ${color}`} />
        <span className="text-xs text-muted-foreground">{label}</span>
      </div>
      <p className="text-xl font-bold text-foreground">{value}</p>
      {sublabel && <p className="text-[10px] text-muted-foreground mt-0.5">{sublabel}</p>}
    </div>
  )
}
